import React, { useState } from 'react';
import { Download, Upload, FileText, AlertCircle, CheckCircle, ArrowRight, Trash2, RefreshCw } from 'lucide-react';

const MergeCSV = () => {
  const [files, setFiles] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [needsMapping, setNeedsMapping] = useState(false);
  const [globalCSV, setGlobalCSV] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };
    
    const headers = lines[0].split(',').map(h => h.trim());
    const rows = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      return headers.reduce((obj, header, idx) => {
        obj[header] = values[idx] || '';
        return obj;
      }, {});
    });
    
    return { headers, rows };
  };

  const removeDuplicates = (rows, headers) => {
    const seen = new Set();
    return rows.filter(row => {
      const rowString = headers.map(h => row[h] || '').join('|');
      if (seen.has(rowString)) {
        return false;
      }
      seen.add(rowString);
      return true;
    });
  };

  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const parsed = await Promise.all(
        uploadedFiles.map(async (file) => {
          const text = await file.text();
          const { headers, rows } = parseCSV(text);
          return { fileName: file.name, headers, rows };
        })
      );

      setFiles(uploadedFiles);
      setParsedData(parsed);

      // Check if all CSVs have the same columns
      const firstHeaders = parsed[0].headers;
      const allSame = parsed.every(csv => 
        csv.headers.length === firstHeaders.length &&
        csv.headers.every(h => firstHeaders.includes(h))
      );

      if (allSame) {
        // Auto-merge if columns are the same
        mergeCSVsDirectly(parsed);
      } else {
        // Find CSV with fewest columns
        const sortedByColumns = [...parsed].sort((a, b) => a.headers.length - b.headers.length);
        setColumns(sortedByColumns[0].headers);
        setGlobalCSV(sortedByColumns[0]);
        setNeedsMapping(true);
        
        // Initialize mapping for other CSVs
        const initialMapping = {};
        sortedByColumns.slice(1).forEach((csv, idx) => {
          initialMapping[idx] = {};
          sortedByColumns[0].headers.forEach(globalHeader => {
            initialMapping[idx][globalHeader] = '';
          });
        });
        setColumnMapping(initialMapping);
      }
    } catch (err) {
      setError('Error parsing CSV files: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const mergeCSVsDirectly = (parsed) => {
    const allRows = parsed.flatMap(csv => csv.rows);
    const headers = parsed[0].headers;
    const uniqueRows = removeDuplicates(allRows, headers);
    
    const duplicateCount = allRows.length - uniqueRows.length;
    setGlobalCSV({ headers, rows: uniqueRows, fileName: 'merged.csv' });
    setSuccess(`Successfully merged ${parsed.length} CSV files with ${uniqueRows.length} unique rows! ${duplicateCount > 0 ? `(${duplicateCount} duplicates removed)` : ''}`);
    setNeedsMapping(false);
  };

  const handleMappingChange = (csvIndex, globalColumn, sourceColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [csvIndex]: {
        ...prev[csvIndex],
        [globalColumn]: sourceColumn
      }
    }));
  };

  const applyMapping = () => {
    setLoading(true);
    setError('');

    try {
      // Start with the base CSV (fewest columns)
      let mergedRows = [...globalCSV.rows];
      const globalHeaders = globalCSV.headers;

      // Get all CSVs except the base one
      const sortedByColumns = [...parsedData].sort((a, b) => a.headers.length - b.headers.length);
      const otherCSVs = sortedByColumns.slice(1);

      // Apply mapping and merge
      otherCSVs.forEach((csv, idx) => {
        const mapping = columnMapping[idx];
        
        csv.rows.forEach(row => {
          const mappedRow = {};
          
          // For each global column, get the value from the mapped source column
          globalHeaders.forEach(globalCol => {
            const sourceCol = mapping[globalCol];
            if (sourceCol && sourceCol !== '') {
              mappedRow[globalCol] = row[sourceCol] || '';
            } else {
              mappedRow[globalCol] = '';
            }
          });
          
          mergedRows.push(mappedRow);
        });
      });

      // Remove duplicates
      const uniqueRows = removeDuplicates(mergedRows, globalHeaders);
      const duplicateCount = mergedRows.length - uniqueRows.length;

      setGlobalCSV({ headers: globalHeaders, rows: uniqueRows, fileName: 'merged.csv' });
      setSuccess(`Successfully merged all CSVs with column mapping! Total unique rows: ${uniqueRows.length}${duplicateCount > 0 ? ` (${duplicateCount} duplicates removed)` : ''}`);
      setNeedsMapping(false);
    } catch (err) {
      setError('Error applying mapping: ' + err.message);
      console.error('Mapping error:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!globalCSV) return;

    const csvContent = [
      globalCSV.headers.join(','),
      ...globalCSV.rows.map(row => 
        globalCSV.headers.map(header => row[header] || '').join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'merged.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetAll = () => {
    setFiles([]);
    setParsedData([]);
    setColumns([]);
    setNeedsMapping(false);
    setGlobalCSV(null);
    setColumnMapping({});
    setError('');
    setSuccess('');
  };

  const isMappingComplete = () => {
    if (!needsMapping) return true;
    
    // Check if all global columns have been mapped for each CSV
    return Object.keys(columnMapping).every(csvIdx => {
      const mapping = columnMapping[csvIdx];
      return columns.every(globalCol => mapping[globalCol] && mapping[globalCol] !== '');
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8" style={{ padding: '8px' }}>
          <h1 className="text-4xl sm:text-5xl font-bold text-black mb-3 tracking-tight">
            CSV Merge Tool
          </h1>
          <p className="text-purple-200 text-lg">
            Intelligently merge multiple CSV files with duplicate detection
          </p>
        </div>

        <div className="bg-black/10 backdrop-blur-xl rounded-3xl shadow-2xl p-6 sm:p-8 border border-black/20">
          {/* File Upload */}
          {!files.length && (
            <div className="mb-8" style={{ padding: '8px' }}>
              <label className="group relative flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-purple-400/50 rounded-2xl cursor-pointer bg-gradient-to-br from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 transition-all duration-300">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <div className="p-4 bg-purple-500/20 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Upload className="w-12 h-12 text-purple-300" />
                  </div>
                  <p className="mb-2 text-lg font-semibold text-black">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-sm text-purple-200">
                    Upload multiple CSV files at once
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".csv"
                  multiple
                  onChange={handleFileUpload}
                />
              </label>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-400/30 border-t-purple-400 rounded-full animate-spin"></div>
              </div>
              <p className="mt-4 text-purple-200 font-medium">Processing your files...</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-start gap-3 backdrop-blur-sm">
              <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
              <p className="text-red-100">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl flex items-start gap-3 backdrop-blur-sm animate-pulse">
              <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
              <p className="text-green-100">{success}</p>
            </div>
          )}

          {/* File List */}
          {files.length > 0 && (
            <div className="mb-8" style={{ padding: '8px' }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-black">Uploaded Files</h3>
                <button
                  onClick={resetAll}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg transition-colors border border-red-500/50"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reset
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {parsedData.map((csv, idx) => (
                  <div key={idx} className="p-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-400/30 hover:border-purple-400/50 transition-all duration-300 hover:scale-105">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-500/30 rounded-lg">
                        <FileText className="w-5 h-5 text-purple-200" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-black font-semibold truncate">{csv.fileName}</p>
                        <div className="flex gap-4 mt-2">
                          <span className="text-xs text-purple-200 bg-purple-500/20 px-2 py-1 rounded">
                            {csv.headers.length} columns
                          </span>
                          <span className="text-xs text-pink-200 bg-pink-500/20 px-2 py-1 rounded">
                            {csv.rows.length} rows
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Column Mapping Interface */}
{/* Column Mapping Interface */}
          {needsMapping && (
            <div className="mb-8" style={{ padding: '8px' }}>
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-black mb-3">Column Mapping Required</h2>
                <p className="text-purple-200">
                  Map columns from each file to match the global structure
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl p-6 mb-8 backdrop-blur-sm" style={{ padding: '8px' }}>
                <h3 className="font-bold text-blue-200 mb-4 text-lg flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  Global CSV Structure (Base: {parsedData.sort((a, b) => a.headers.length - b.headers.length)[0]?.fileName})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {columns.map((col, idx) => (
                    <span key={idx} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-black rounded-full text-sm font-semibold shadow-lg">
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              {parsedData
                .sort((a, b) => a.headers.length - b.headers.length)
                .slice(1)
                .map((csv, csvIdx) => (
                  <div key={csvIdx} className="mb-6 p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm">
                    <h3 className="font-bold text-black mb-6 flex items-center gap-3 text-lg">
                      <div className="p-2 bg-purple-500/30 rounded-lg">
                        <FileText className="w-5 h-5 text-purple-200" />
                      </div>
                      <div>
                        <div>{csv.fileName}</div>
                        <div className="text-xs text-purple-300 font-normal">Map these columns to global structure</div>
                      </div>
                    </h3>
                    
                    {/* Table Layout for Mapping */}
                    <div className="overflow-x-auto rounded-xl border-purple-400/30">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gradient-to-r from-purple-500/30 to-pink-500/30">
                            <th className="px-6 py-4 text-left text-black font-bold border-b border-purple-400/30">
                              Global Column
                            </th>
                            <th className="px-6 py-4 text-center text-black font-bold border-b border-purple-400/30 w-24">
                              
                            </th>
                            <th className="px-6 py-4 text-left text-black font-bold border-b border-purple-400/30">
                              Source Column from {csv.fileName}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {columns.map((globalColumn, headerIdx) => (
                            <tr 
                              key={headerIdx} 
                              className="hover:bg-purple-500/10 transition-colors border-b border-purple-400/10 last:border-b-0"
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                  <span className="text-black font-medium">{globalColumn}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <ArrowRight className="w-5 h-5 text-purple-300 mx-auto" />
                              </td>
                              <td className="px-6 py-4">
                                <select
                                  className="w-full p-3 border border-purple-400/30 rounded-lg bg-black/10 text-black backdrop-blur-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none cursor-pointer hover:bg-black/20"
                                  value={columnMapping[csvIdx]?.[globalColumn] || ''}
                                  onChange={(e) => handleMappingChange(csvIdx, globalColumn, e.target.value)}
                                >
                                  <option value="" className="bg-slate-800">Select column...</option>
                                  {csv.headers.map((col, colIdx) => (
                                    <option key={colIdx} value={col} className="bg-slate-800">{col}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

<table className="w-full">
  <tbody>
    <tr 
                              className="hover:bg-purple-500/10 transition-colors border-b border-purple-400/10 last:border-b-0"
                            >
<td className="px-6 py-4">
  <span className="invisible">Apply Map Merge</span>
</td>


                              <td className="px-6 py-4">
                          <button
                            onClick={applyMapping}
                            disabled={!isMappingComplete() || loading}
                            className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-black font-bold rounded-xl hover:from-purple-600 hover:to-pink-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-purple-500/50 text-lg"
                          >
                            {loading ? 'Processing...' : 'Apply Mapping and Merge'}
                          </button>
                              </td>
                            </tr>
  </tbody>
</table>

            </div>
          )}

          {/* Download Button */}
          {globalCSV && !needsMapping && (
            <div className="mt-8 space-y-4">
              <div className="p-6 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-400/30 rounded-2xl backdrop-blur-sm">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-black mb-2">Merge Complete!</h3>
                    <p className="text-green-200">
                      Your CSV is ready with <span className="font-bold text-black">{globalCSV.rows.length}</span> unique rows
                    </p>
                  </div>
                  <button
                    onClick={downloadCSV}
                    className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-black font-bold rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-lg hover:shadow-green-500/50 hover:scale-105"
                  >
                    <Download className="w-5 h-5" />
                    Download Merged CSV
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-purple-300 text-sm">
            Automatically removes duplicate rows and intelligently merges your data
          </p>
        </div>
      </div>
    </div>
  );
};

export default MergeCSV;